import { API_BASE_URL } from '../config/api';
import { throwForCreditGate } from './entitlementErrors';
import type { GaAnalyticsPayload } from './ga4Client';

interface AudioGenerationRequest {
  prompt: string;
  style: string;
  title: string;
  analytics?: GaAnalyticsPayload;
}

export const generateAudio = async (
  data: AudioGenerationRequest,
  token: string
): Promise<ReadableStream<Uint8Array> | null> => {
  try {
    const requestId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const response = await fetch(`${API_BASE_URL}/generate/audio`, {
      method: 'POST',
      headers: {
        'x-request-id': requestId,
        Accept: 'text/event-stream',
        Connection: 'keep-alive',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.text();
      // 402/403 throw the shared typed errors (same upsell handling as video).
      throwForCreditGate(response.status, errorData, {
        insufficient: 'Insufficient pixels for this audio',
        noEntitlement: 'An active subscription is required for this audio',
        failed: 'Failed to generate audio',
      });
    }

    // Return the readable stream for event processing
    return response.body;
  } catch (error: any) {
    console.error('Audio Generation Error:', error);
    throw error;
  }
};

export interface GeneratedAudioResult {
  // Full MP3 served by the backend proxy (`audioUrl`).
  audioUrl: string;
  // Persistent CDN source MP3 from Suno (`sourceAudioUrl`).
  sourceAudioUrl: string;
  // Streaming audio URL returned by the backend (`streamAudioUrl`).
  streamAudioUrl: string;
  // Streaming source URL from Suno (`sourceStreamAudioUrl`).
  sourceStreamAudioUrl: string;
  // Track length in seconds returned by the generation API (`duration`).
  duration?: number;
}

export const handleAudioGeneration = async (
  data: AudioGenerationRequest,
  token: string,
  onEvent: (event: any) => void,
  onComplete: (result?: GeneratedAudioResult) => void
): Promise<GeneratedAudioResult> => {
  try {
    const stream = await generateAudio(data, token);

    if (!stream) {
      throw new Error('No stream returned from API');
    }

    const reader = stream.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let audioUrl = '';
    let sourceAudioUrl = '';
    let streamAudioUrl = '';
    let sourceStreamAudioUrl = '';
    let duration: number | undefined;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        const result: GeneratedAudioResult = {
          audioUrl,
          sourceAudioUrl,
          streamAudioUrl,
          sourceStreamAudioUrl,
          duration,
        };
        onComplete(result);
        return result;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim() === '') continue;

        if (line.startsWith('data:')) {
          const eventData = line.slice(5).trim();
          try {
            const parsedData = JSON.parse(eventData);
            onEvent(parsedData);

            // Check if this is the completion event with audio data
            if (
              parsedData.data &&
              Array.isArray(parsedData.data) &&
              parsedData.data.length > 0
            ) {
              const item = parsedData.data[0];
              // Capture all audio URLs returned by the backend so callers can
              // use the full MP3 for preview, the persistent CDN source for
              // storage/playback, and the dedicated streaming URLs as needed.
              audioUrl = item.audioUrl || '';
              sourceAudioUrl = item.sourceAudioUrl || '';
              streamAudioUrl = item.streamAudioUrl || '';
              sourceStreamAudioUrl = item.sourceStreamAudioUrl || '';
              duration =
                typeof item.duration === 'number' ? item.duration : undefined;
            }
          } catch (e) {
            console.warn('Failed to parse event data:', eventData);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in audio generation stream:', error);
    throw error;
  }
};
